import {
  BookingPromoIneligibilityReason,
  TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY,
  translate,
} from '@cfixe-monorepo/shared';
import type { BookingLabels } from '../types';

export type PromoIneligibilityLabels = Pick<BookingLabels, 'promo_error_invalid' | 'code_error_not_found'>;

/**
 * Libellé client d'une raison d'inéligibilité d'un code promo (fr-FR, même source que les erreurs API
 * du tunnel). Code introuvable : libellé neutre du tunnel, le champ unique « Code promo ou carte
 * cadeau » a essayé les deux. Repli sur `promo_error_invalid` si la raison n'a pas de libellé.
 */
export function resolvePromoIneligibilityMessage(
  reason: BookingPromoIneligibilityReason,
  labels: PromoIneligibilityLabels,
): string {
  if (reason === BookingPromoIneligibilityReason.CODE_NOT_FOUND) return labels.code_error_not_found;
  return (
    translate(
      'fr-FR',
      TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY,
      `${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${reason}`,
    ) ?? labels.promo_error_invalid
  );
}

/** Clé d'erreur du refus d'un code promo par la pose ferme (PATCH /promo, création de pré-réservation). */
export const PROMO_ERROR_KEY = {
  NOT_ACTIVE: 'PRE_RESERVATION_PROMO_NOT_ACTIVE',
  OUTSIDE_HOURS: 'PRE_RESERVATION_PROMO_OUTSIDE_HOURS',
} as const;

/**
 * Clé d'erreur de la pose ferme → raison d'inéligibilité renvoyée par cart-summary (réciproque de
 * `PROMO_REASON_TO_ERROR` côté backend). Table unique : avant comme après la pré-réservation, le
 * client lit le même motif. Deux clés n'y figurent pas, elles ont leur libellé du tunnel :
 * NOT_ACTIVE (couvre deux raisons, code inactif OU hors période) et OUTSIDE_HOURS (le serveur
 * fournit les horaires du code).
 */
export const PROMO_ERROR_KEY_TO_REASON: Readonly<Record<string, BookingPromoIneligibilityReason>> = {
  PRE_RESERVATION_PROMO_CODE_NOT_FOUND: BookingPromoIneligibilityReason.CODE_NOT_FOUND,
  PRE_RESERVATION_PROMO_NOT_APPLICABLE: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_CART,
  PRE_RESERVATION_PROMO_ZERO_DISCOUNT: BookingPromoIneligibilityReason.ZERO_DISCOUNT,
  PRE_RESERVATION_PROMO_MIN_CART: BookingPromoIneligibilityReason.MIN_CART_AMOUNT_NOT_MET,
  PRE_RESERVATION_PROMO_FIRST_TIME_ONLY: BookingPromoIneligibilityReason.NOT_FIRST_TIME_CUSTOMER,
  PRE_RESERVATION_PROMO_MAX_REDEMPTIONS: BookingPromoIneligibilityReason.MAX_REDEMPTIONS_REACHED,
  PRE_RESERVATION_PROMO_MAX_PER_CUSTOMER: BookingPromoIneligibilityReason.MAX_PER_CUSTOMER_REACHED,
  PRE_RESERVATION_PROMO_PERSONAL_CODE_ALREADY_USED: BookingPromoIneligibilityReason.PERSONAL_CODE_ALREADY_USED,
  PRE_RESERVATION_PROMO_PERSONAL_CODE_NOT_FOR_CUSTOMER: BookingPromoIneligibilityReason.PERSONAL_CODE_NOT_FOR_CUSTOMER,
  PRE_RESERVATION_PROMO_VOUCHER_EXCLUSIVE: BookingPromoIneligibilityReason.PROMO_VOUCHER_NOT_CUMULABLE,
  PRE_RESERVATION_PROMO_BETTER_OFFER_APPLIED: BookingPromoIneligibilityReason.BETTER_OFFER_APPLIED,
  PRE_RESERVATION_PROMO_CUSTOMER_NOT_TARGETED: BookingPromoIneligibilityReason.CUSTOMER_NOT_TARGETED,
  PRE_RESERVATION_PROMO_NOT_APPLICABLE_TO_PLACE: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_PLACE,
  PRE_RESERVATION_PROMO_NOT_APPLICABLE_TO_MEMBER: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_MEMBER,
};

export type PromoErrorLabels = PromoIneligibilityLabels &
  Pick<BookingLabels, 'promo_error_expired' | 'promo_error_outside_hours'>;

/**
 * Message affiché pour un code promo refusé par le serveur. La phrase serveur destinée au client
 * (`data.user_message`, ex. les horaires du code) passe en premier ; sinon le libellé du motif.
 */
export function resolvePromoErrorMessage(
  errorKey: string | undefined,
  serverMessage: string | undefined,
  labels: PromoErrorLabels,
): string {
  if (serverMessage) return serverMessage;
  if (errorKey === PROMO_ERROR_KEY.NOT_ACTIVE) return labels.promo_error_expired;
  if (errorKey === PROMO_ERROR_KEY.OUTSIDE_HOURS) return labels.promo_error_outside_hours;
  const reason = errorKey !== undefined ? PROMO_ERROR_KEY_TO_REASON[errorKey] : undefined;
  return reason ? resolvePromoIneligibilityMessage(reason, labels) : labels.promo_error_invalid;
}
